// Share cards (AWARDS.md "Share cards"): a square image drawn on a canvas
// from the same view the screen renders, handed to the phone's share sheet
// when the browser allows files, else downloaded. No server, no cost.

import { BRAND } from '../branding';

export interface ShareCardInput {
  /** "HIGH SCORE", "CHAMPION", "TROPHY ROOM". */
  kicker: string;
  /** The big line: "131.2", the team name, or the owner. */
  headline: string;
  /** Under the headline: "Dave's Packers · Dave". */
  subline: string;
  /** The sentence, wrapped. */
  line: string;
  /** "Ken's League · 2026". */
  footer: string;
  team: { abbreviation: string; colors: { primary: string; secondary: string } };
  tone: 'glory' | 'shame' | 'fun';
}

const SIZE = 1080;
const TONE: Record<ShareCardInput['tone'], string> = {
  glory: '#f2c14e',
  shame: '#e07a8c',
  fun: '#7ad0ff',
};

function wrap(g: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    const next = current ? `${current} ${w}` : w;
    if (g.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else current = next;
  }
  if (current) lines.push(current);
  return lines;
}

function onColor(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 150 ? '#101418' : '#ffffff';
}

/** Draw the card. Returns the canvas so the sheet can preview it. */
export function drawShareCard(input: ShareCardInput): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const g = canvas.getContext('2d')!;
  const { primary, secondary } = input.team.colors;

  // Ground: night, with the team's colors washing in from the corners.
  g.fillStyle = '#0d1117';
  g.fillRect(0, 0, SIZE, SIZE);
  const glow = g.createRadialGradient(140, 120, 40, 140, 120, 900);
  glow.addColorStop(0, `${primary}dd`);
  glow.addColorStop(0.55, `${primary}33`);
  glow.addColorStop(1, `${primary}00`);
  g.fillStyle = glow;
  g.fillRect(0, 0, SIZE, SIZE);
  const glow2 = g.createRadialGradient(SIZE - 100, SIZE - 80, 20, SIZE - 100, SIZE - 80, 700);
  glow2.addColorStop(0, `${secondary}99`);
  glow2.addColorStop(1, `${secondary}00`);
  g.fillStyle = glow2;
  g.fillRect(0, 0, SIZE, SIZE);

  // Team mark.
  g.beginPath();
  g.arc(150, 150, 70, 0, Math.PI * 2);
  g.fillStyle = primary;
  g.fill();
  g.lineWidth = 8;
  g.strokeStyle = secondary;
  g.stroke();
  g.fillStyle = onColor(primary);
  g.font = 'bold 52px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(input.team.abbreviation.slice(0, 3), 150, 154);

  // Kicker.
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  g.fillStyle = TONE[input.tone];
  g.font = 'bold 40px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  g.fillText(input.kicker.toUpperCase(), 260, 130);

  // Headline.
  g.fillStyle = '#ffffff';
  g.font = 'bold 150px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  let headline = input.headline;
  while (g.measureText(headline).width > SIZE - 160 && headline.length > 3)
    headline = `${headline.slice(0, -2)}…`;
  g.fillText(headline, 80, 420);

  // Subline.
  g.fillStyle = '#c9d1d9';
  g.font = '600 46px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  g.fillText(input.subline, 84, 500);

  // The line.
  g.fillStyle = '#ffffff';
  g.font = '500 54px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const lines = wrap(g, input.line, SIZE - 170).slice(0, 5);
  lines.forEach((l, i) => g.fillText(l, 84, 620 + i * 72));

  // Footer.
  g.fillStyle = '#8b949e';
  g.font = '600 34px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  g.fillText(input.footer, 84, SIZE - 70);
  g.textAlign = 'right';
  g.fillStyle = TONE[input.tone];
  g.font = 'bold 34px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  g.fillText(BRAND.shortName, SIZE - 80, SIZE - 70);
  return canvas;
}

/** A team as the class photo draws it: its mark, its name, its tag. */
interface PhotoTeam {
  name: string;
  tag: string;
  tone: ShareCardInput['tone'];
  team: ShareCardInput['team'];
}

/**
 * The yearbook's class photo (PLAN-2026-10-31-the-yearbook §2): the season,
 * the league, the champion large, and every team's mark in a grid with its
 * name and its tag. Taller than the square card, 4:5, because a group chat
 * and a phone's photos both show that whole.
 */
export interface ClassPhotoInput {
  /** "2026 yearbook". */
  kicker: string;
  /** The league's name. */
  title: string;
  champion: { name: string; title: string; team: ShareCardInput['team'] };
  /** The class, the champion first, as the yearbook lists it. */
  teams: PhotoTeam[];
  footer: string;
}

const PHOTO_H = 1350;
const FONT = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

function fit(g: CanvasRenderingContext2D, text: string, max: number): string {
  let t = text;
  while (g.measureText(t).width > max && t.length > 2) t = `${t.slice(0, -2)}…`;
  return t;
}

function mark(
  g: CanvasRenderingContext2D,
  team: ShareCardInput['team'],
  x: number,
  y: number,
  r: number,
): void {
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fillStyle = team.colors.primary;
  g.fill();
  g.lineWidth = Math.max(3, r / 9);
  g.strokeStyle = team.colors.secondary;
  g.stroke();
  g.fillStyle = onColor(team.colors.primary);
  g.font = `bold ${Math.round(r * 0.72)}px ${FONT}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(team.abbreviation.slice(0, 3), x, y + r * 0.05);
}

export function drawClassPhoto(input: ClassPhotoInput): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = PHOTO_H;
  const g = canvas.getContext('2d')!;
  const { primary, secondary } = input.champion.team.colors;

  // Ground: night, the champion's colors washing in from the top corner.
  g.fillStyle = '#0d1117';
  g.fillRect(0, 0, SIZE, PHOTO_H);
  const glow = g.createRadialGradient(170, 330, 40, 170, 330, 820);
  glow.addColorStop(0, `${primary}cc`);
  glow.addColorStop(0.5, `${primary}2a`);
  glow.addColorStop(1, `${primary}00`);
  g.fillStyle = glow;
  g.fillRect(0, 0, SIZE, PHOTO_H);
  const glow2 = g.createRadialGradient(SIZE - 80, 80, 20, SIZE - 80, 80, 520);
  glow2.addColorStop(0, `${secondary}77`);
  glow2.addColorStop(1, `${secondary}00`);
  g.fillStyle = glow2;
  g.fillRect(0, 0, SIZE, PHOTO_H);

  // The season and the league.
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  g.fillStyle = TONE.glory;
  g.font = `bold 38px ${FONT}`;
  g.fillText(input.kicker.toUpperCase(), 80, 112);
  g.fillStyle = '#ffffff';
  g.font = `bold 76px ${FONT}`;
  g.fillText(fit(g, input.title, SIZE - 160), 80, 196);

  // The champion, large.
  mark(g, input.champion.team, 172, 372, 92);
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  g.fillStyle = TONE.glory;
  g.font = `bold 32px ${FONT}`;
  g.fillText('CHAMPION', 300, 330);
  g.fillStyle = '#ffffff';
  g.font = `bold 64px ${FONT}`;
  g.fillText(fit(g, input.champion.name, SIZE - 380), 300, 402);
  g.fillStyle = '#c9d1d9';
  g.font = `600 34px ${FONT}`;
  g.fillText(fit(g, input.champion.title, SIZE - 380), 300, 452);

  // The class: every team, in rows of up to four (five past sixteen), the
  // last row centered.
  g.strokeStyle = '#30363d';
  g.lineWidth = 2;
  g.beginPath();
  g.moveTo(80, 540);
  g.lineTo(SIZE - 80, 540);
  g.stroke();
  g.fillStyle = '#8b949e';
  g.font = `bold 28px ${FONT}`;
  g.fillText('THE CLASS', 80, 596);
  const n = input.teams.length;
  const cols = n > 16 ? 5 : n > 6 ? 4 : Math.max(1, Math.min(3, n));
  const rows = Math.ceil(n / cols);
  const top = 628;
  const bottom = PHOTO_H - 130;
  const cellW = (SIZE - 160) / cols;
  const cellH = Math.min(196, (bottom - top) / Math.max(1, rows));
  const r = Math.min(46, cellH * 0.25);
  const small = rows >= 4;
  input.teams.forEach((t, i) => {
    const row = Math.floor(i / cols);
    const inRow = row === rows - 1 ? n - row * cols : cols;
    const col = i % cols;
    const x = 80 + (SIZE - 160 - inRow * cellW) / 2 + col * cellW + cellW / 2;
    const y = top + row * cellH;
    mark(g, t.team, x, y + 8 + r, r);
    g.textAlign = 'center';
    g.textBaseline = 'alphabetic';
    g.fillStyle = '#ffffff';
    g.font = `bold ${small ? 23 : 26}px ${FONT}`;
    const nameY = y + 8 + 2 * r + (small ? 30 : 36);
    g.fillText(fit(g, t.name, cellW - 16), x, nameY);
    g.fillStyle = TONE[t.tone];
    g.font = `600 ${small ? 21 : 23}px ${FONT}`;
    g.fillText(fit(g, t.tag, cellW - 16), x, nameY + (small ? 26 : 30));
  });

  // Footer.
  g.textAlign = 'left';
  g.fillStyle = '#8b949e';
  g.font = `600 34px ${FONT}`;
  g.fillText(fit(g, input.footer, SIZE - 420), 84, PHOTO_H - 70);
  g.textAlign = 'right';
  g.fillStyle = TONE.glory;
  g.font = `bold 34px ${FONT}`;
  g.fillText(BRAND.shortName, SIZE - 80, PHOTO_H - 70);
  return canvas;
}

/**
 * The League page's team photo as a picture (PLAN-2026-10-31-a-handshake-a-
 * dance-and-the-photo §3): the league and the week, every team's character
 * where it stands on the screen, and under the photo who is who, row by row,
 * as a team photo's caption says it. 4:5, as the class photo is.
 */
export interface TeamPhotoInput {
  /** "Week 8 · 2026". */
  kicker: string;
  /** The league's name. */
  title: string;
  /** Each row left to right, as the screen stands it; the back row empty in a league of one. */
  back: PhotoPerson[];
  front: PhotoPerson[];
}

export interface PhotoPerson {
  /** As the caption names it: "Rockets", "Monsoon (champion)". */
  name: string;
  team: ShareCardInput['team'];
  /** The character as the screen draws it, or null for a team with no face. */
  drawing: SVGSVGElement | null;
  /** The week's hat perched on a placard's badge, as the screen draws it. */
  hat: SVGSVGElement | null;
  /** A bot's tag (B-488), its glyph as the screen draws it. */
  bot: SVGSVGElement | null;
}

/** The character's box, width over height (FigureArt's FIGURE_BOX). */
const FIGURE_RATIO = 124 / 172;
/** How far the front row stands up over the back row's knees, in widths (league-front.css). */
const FRONT_UP = 0.62;

/**
 * THE FACE'S OWN RULES, CARRIED IN: a mood's mouth and brows are the site's
 * stylesheet's (kit.css), so a drawing taken off the page wears its expression
 * only with them. Read from the page's sheets rather than copied, and with
 * nothing moving, since a picture is one moment.
 */
function faceRules(): string {
  const out: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    for (const rule of Array.from(rules)) {
      if (rule instanceof CSSStyleRule && /fsp-(botface|figure|bf-|fig-)/.test(rule.selectorText))
        out.push(rule.cssText);
    }
  }
  out.push('*{animation:none!important;transition:none!important}');
  return out.join('\n');
}

/**
 * A drawing on the page as an image the canvas can take, at the size it is
 * drawn; `ink` is the color a glyph drawn in the text's color takes. One that
 * cannot be drawn fails the picture rather than leaving somebody out of it.
 */
async function drawingImage(
  svg: SVGSVGElement,
  w: number,
  h: number,
  rules: string,
  ink?: string,
): Promise<HTMLImageElement> {
  const copy = svg.cloneNode(true) as SVGSVGElement;
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  copy.setAttribute('width', String(Math.round(w)));
  copy.setAttribute('height', String(Math.round(h)));
  if (ink !== undefined) copy.setAttribute('style', `color:${ink};stroke-width:2`);
  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  style.textContent = rules;
  copy.insertBefore(style, copy.firstChild);
  const img = new Image();
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(copy))}`;
  await img.decode();
  return img;
}

/** A drawing's width over its height, from its box. */
function aspectOf(svg: SVGSVGElement): number {
  const box = svg.viewBox.baseVal;
  return box && box.width > 0 && box.height > 0 ? box.width / box.height : 1;
}

export async function drawTeamPhoto(input: TeamPhotoInput): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = PHOTO_H;
  const g = canvas.getContext('2d')!;
  const ink = '#1f1a17';

  // A print: warm paper, the league and the week across the top.
  g.fillStyle = '#f6f3ec';
  g.fillRect(0, 0, SIZE, PHOTO_H);
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  g.fillStyle = '#8a6d2f';
  g.font = `bold 34px ${FONT}`;
  g.fillText(input.kicker.toUpperCase(), 64, 112);
  g.fillStyle = ink;
  g.font = `bold 72px ${FONT}`;
  g.fillText(fit(g, input.title, SIZE - 128), 64, 190);

  // WHO IS WHO, under the photo, a row at a time from the back, left to right.
  const rows = [input.back, input.front].filter((r) => r.length > 0);
  g.font = `600 30px ${FONT}`;
  const said =
    rows.length === 1
      ? [`Left to right: ${rows[0]!.map((p) => p.name).join(', ')}.`]
      : [
          `Back row: ${input.back.map((p) => p.name).join(', ')}.`,
          `Front row: ${input.front.map((p) => p.name).join(', ')}.`,
        ];
  const caption = said.map((line) => wrap(g, line, SIZE - 128));
  const captionH = caption.reduce((n, lines) => n + lines.length * 40 + 12, 0);

  // The photo: as wide as the page allows, the people as large as fits with
  // some sky over them, and the photo and its caption in the middle of what
  // the title and the foot leave.
  const panelX = 48;
  const panelW = SIZE - 96;
  const top = 236;
  const foot = PHOTO_H - 110;
  const padBottom = 28;
  const gap = 44;
  const perRow = Math.max(input.front.length, input.back.length, 1);
  const tall = rows.length === 2 ? 2 / FIGURE_RATIO - FRONT_UP : 1 / FIGURE_RATIO;
  const room = foot - top - captionH - gap - padBottom - 80;
  const w = Math.min((panelW - 48) / perRow, room / 1.35 / tall, 300);
  const h = w / FIGURE_RATIO;
  const people = w * tall;
  const padTop = Math.max(80, Math.min(people * 0.35, room - people));
  const panelH = padTop + people + padBottom;
  const panelTop = top + Math.max(0, (foot - top - (panelH + gap + captionH)) / 2);

  g.save();
  g.beginPath();
  g.roundRect(panelX, panelTop, panelW, panelH, 24);
  g.clip();
  const sky = g.createLinearGradient(0, panelTop, 0, panelTop + panelH);
  sky.addColorStop(0, '#bcd7ef');
  sky.addColorStop(1, '#eef4fa');
  g.fillStyle = sky;
  g.fillRect(panelX, panelTop, panelW, panelH);
  const groundY = panelTop + panelH - padBottom - h * 0.28;
  const grass = g.createLinearGradient(0, groundY, 0, panelTop + panelH);
  grass.addColorStop(0, '#8cc274');
  grass.addColorStop(1, '#5e9a4c');
  g.fillStyle = grass;
  g.fillRect(panelX, groundY, panelW, panelTop + panelH - groundY);

  const rules = faceRules();
  const stand = async (row: PhotoPerson[], top: number) => {
    const left = panelX + (panelW - row.length * w) / 2;
    // Sizes as the screen's: a placard's badge 62% of the width and its hat
    // 1.05 of the badge (kit.css, TeamMark); a bot's tag 22% of the width.
    const r = w * 0.31;
    const hatW = 2 * r * 1.05;
    const tag = w * 0.22;
    const drawn = await Promise.all(
      row.map(async (p) => ({
        figure: p.drawing === null ? null : await drawingImage(p.drawing, w, h, rules),
        hat: p.hat === null ? null : await drawingImage(p.hat, hatW, hatW / aspectOf(p.hat), rules),
        bot: p.bot === null ? null : await drawingImage(p.bot, tag, tag, '', '#ffffff'),
      })),
    );
    row.forEach((p, i) => {
      const x = left + i * w;
      const { figure, hat, bot } = drawn[i]!;
      if (figure !== null) g.drawImage(figure, x, top, w, h);
      else {
        // A TEAM WITH NO FACE as its badge on a pole, as the screen's placard,
        // with the week's hat perched on it.
        const cx = x + w / 2;
        const cy = top + h * 0.12 + r;
        g.fillStyle = '#6b6560';
        g.fillRect(cx - Math.max(2, w * 0.012), cy, Math.max(4, w * 0.024), h * 0.9 - (cy - top));
        mark(g, p.team, cx, cy, r);
        if (hat !== null) {
          const hatH = hatW / aspectOf(p.hat!);
          g.save();
          g.translate(cx, cy - r - 0.14 * hatH);
          g.rotate((-9 * Math.PI) / 180);
          g.drawImage(hat, -hatW / 2, -hatH / 2, hatW, hatH);
          g.restore();
        }
      }
      if (bot !== null) {
        // A BOT'S TAG up by its head, as the screen's (kit.css .fsp-teamfigure-bot).
        const tx = x + w * 0.98 - tag / 2;
        const ty = top + h * 0.04 + tag / 2;
        g.beginPath();
        g.arc(tx, ty, tag / 2, 0, Math.PI * 2);
        g.fillStyle = '#1f1a17';
        g.fill();
        g.lineWidth = Math.max(2, tag * 0.08);
        g.strokeStyle = '#ffffff';
        g.stroke();
        g.drawImage(bot, tx - tag * 0.38, ty - tag * 0.38, tag * 0.76, tag * 0.76);
      }
    });
  };
  if (rows.length === 2) {
    await stand(input.back, panelTop + padTop);
    await stand(input.front, panelTop + padTop + h - FRONT_UP * w);
  } else await stand(rows[0] ?? [], panelTop + padTop);
  g.restore();

  // The caption.
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  g.fillStyle = '#3b3530';
  g.font = `600 30px ${FONT}`;
  let y = panelTop + panelH + gap + 12;
  for (const lines of caption) {
    for (const line of lines) {
      g.fillText(line, 64, y);
      y += 40;
    }
    y += 12;
  }

  // Footer.
  g.textAlign = 'right';
  g.fillStyle = '#8a6d2f';
  g.font = `bold 34px ${FONT}`;
  g.fillText(BRAND.shortName, SIZE - 64, PHOTO_H - 60);
  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

/** Whether the phone's share sheet can take the image itself. */
export function canShareFiles(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.canShare !== 'function') return false;
  try {
    const probe = new File([new Uint8Array(1)], 'probe.png', { type: 'image/png' });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

/** Hand the card to the share sheet with the text; the caller falls back to download when this returns false. */
export async function shareCard(
  canvas: HTMLCanvasElement,
  text: string,
  title: string,
): Promise<boolean> {
  if (!canShareFiles()) return false;
  const blob = await canvasToBlob(canvas);
  if (!blob) return false;
  const file = new File([blob], `${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`, {
    type: 'image/png',
  });
  try {
    await navigator.share({ files: [file], text, title });
    return true;
  } catch {
    // Dismissed or refused: the sheet still has Download and Copy.
    return false;
  }
}

export function downloadCanvas(canvas: HTMLCanvasElement, title: string): void {
  const a = document.createElement('a');
  a.href = canvas.toDataURL('image/png');
  a.download = `${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
